import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Plus, Trash2, ShoppingCart, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShoppingPage() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeList, setActiveList] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [newItem, setNewItem] = useState('');

  useEffect(() => {
    fetchLists();
  }, []);

  const fetchLists = async () => {
    try {
      const res = await axios.get(`${API}/api/shopping`, { headers: getAuthHeader() });
      setLists(res.data);
      if (res.data.length > 0 && !activeList) {
        setActiveList(res.data[0]);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    try {
      const res = await axios.post(`${API}/api/shopping`, { name: newListName, items: [] }, { headers: getAuthHeader() });
      setLists([...lists, res.data]);
      setActiveList(res.data);
      setNewListName('');
      toast.success('Lista skapad');
    } catch (error) {
      toast.error('Kunde inte skapa lista');
    }
  };

  const addItem = async () => {
    if (!newItem.trim() || !activeList) return;
    const updatedItems = [...(activeList.items || []), { name: newItem, checked: false }];
    try {
      await axios.put(`${API}/api/shopping/${activeList.id}`, { items: updatedItems }, { headers: getAuthHeader() });
      const updated = { ...activeList, items: updatedItems };
      setActiveList(updated);
      setLists(lists.map(l => l.id === activeList.id ? updated : l));
      setNewItem('');
    } catch (error) {
      toast.error('Kunde inte lägga till');
    }
  };

  const toggleItem = async (index) => {
    if (!activeList) return;
    const updatedItems = activeList.items.map((item, i) => 
      i === index ? { ...item, checked: !item.checked } : item
    );
    try {
      await axios.put(`${API}/api/shopping/${activeList.id}`, { items: updatedItems }, { headers: getAuthHeader() });
      const updated = { ...activeList, items: updatedItems };
      setActiveList(updated);
      setLists(lists.map(l => l.id === activeList.id ? updated : l));
    } catch (error) {
      toast.error('Kunde inte uppdatera');
    }
  };

  const deleteItem = async (index) => {
    if (!activeList) return;
    const updatedItems = activeList.items.filter((_, i) => i !== index);
    try {
      await axios.put(`${API}/api/shopping/${activeList.id}`, { items: updatedItems }, { headers: getAuthHeader() });
      const updated = { ...activeList, items: updatedItems };
      setActiveList(updated);
      setLists(lists.map(l => l.id === activeList.id ? updated : l));
    } catch (error) {
      toast.error('Kunde inte ta bort');
    }
  };

  const deleteList = async (listId) => {
    try {
      await axios.delete(`${API}/api/shopping/${listId}`, { headers: getAuthHeader() });
      const remaining = lists.filter(l => l.id !== listId);
      setLists(remaining);
      setActiveList(remaining[0] || null);
      toast.success('Lista borttagen');
    } catch (error) {
      toast.error('Kunde inte ta bort');
    }
  };

  const checkedCount = activeList?.items?.filter(i => i.checked).length || 0;
  const totalCount = activeList?.items?.length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="shopping-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Inköpslistor</h1>
            {activeList && <p className="text-xs text-muted-foreground">{checkedCount} av {totalCount} köpt</p>}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Lists tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {lists.map(list => (
            <Button
              key={list.id}
              variant={activeList?.id === list.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveList(list)}
              className="rounded-full whitespace-nowrap"
            >
              <ShoppingCart className="w-4 h-4 mr-1" />
              {list.name}
            </Button>
          ))}
          <div className="flex gap-2">
            <Input
              placeholder="Namn på ny lista..."
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && createList()}
              className="rounded-full h-9 w-40"
            />
            <Button size="sm" onClick={createList} className="rounded-full px-3">
              <Plus className="w-4 h-4 mr-1" />
              Skapa lista
            </Button>
          </div>
        </div>

        {/* Active list */}
        {activeList ? (
          <div className="space-y-4">
            {/* Progress */}
            {totalCount > 0 && (
              <div className="bg-white rounded-xl p-3 border border-border/30">
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all"
                    style={{ width: `${(checkedCount / totalCount) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Add item */}
            <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-2">
              <p className="text-sm font-medium text-foreground">Lägg till en vara i listan:</p>
              <div className="flex gap-2">
                <Input
                  placeholder="T.ex. Mjölk, bröd, ägg ..."
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addItem()}
                  className="rounded-xl flex-1"
                />
                <Button onClick={addItem} className="rounded-xl px-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Lägg till
                </Button>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-2">
              {activeList.items?.map((item, index) => (
                <div 
                  key={index}
                  className={`bg-white rounded-xl p-3 border border-border/30 flex items-center gap-3 ${item.checked ? 'opacity-60' : ''}`}
                >
                  <Checkbox 
                    checked={item.checked} 
                    onCheckedChange={() => toggleItem(index)}
                  />
                  <span className={`flex-1 ${item.checked ? 'line-through text-muted-foreground' : ''}`}>
                    {item.name}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => deleteItem(index)}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
              {(!activeList.items || activeList.items.length === 0) && (
                <p className="text-center text-muted-foreground py-8">Listan är tom</p>
              )}
            </div>

            {/* Delete list */}
            <Button 
              variant="outline" 
              onClick={() => deleteList(activeList.id)}
              className="w-full rounded-xl text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Ta bort lista
            </Button>
          </div>
        ) : (
          <div className="text-center py-12">
            <ShoppingCart className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Skapa din första inköpslista</p>
          </div>
        )}
      </main>
    </div>
  );
}
